import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import VendorLayout from '../layouts/vendor';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import UserGuestGuard, {
  LinguistGuestGuard,
  ClientGuestGuard
} from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import useAuth from '../hooks/useAuth';
// The three sign-in pages are imported, not lazy: AuthGuard renders them
// inline, so they are in the main bundle already, and a lazy wrapper only put
// a Suspense fallback (the template loader) between a guest guard's pending
// sky and the card that follows it.
import Login from '../pages/auth/Login';
import LinguistLogin from '../pages/auth/linguistAuth/Login';
import ClientLogin from '../pages/auth/clientAuth/Login';
// The public folder page a client opens from a delivery link is imported,
// not lazy, for the same reason: its query leaves the moment the app starts
// instead of after one more chunk, and public/index.html has already painted
// its frame, which a Suspense loader would replace for a moment.
import TranslatedFiles from '../pages/TranslatedFiles';
// import TypeBasedGuard from '../guards/TypeBasedGuard';
// config
import { PATH_AFTER_LOGIN, PATH_AFTER_LOGIN_LINGUIST } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';
// ----------------------------------------------------------------------

const NotificationOpen = lazy(() => import('src/pages/dashboard/NotificationOpen'));

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense
      fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const { user } = useAuth();
  return useRoutes([
    {
      path: 'intranet',
      children: [
        {
          index: true,
          element: (
            <UserGuestGuard userType={'user'}>
              <Login />
            </UserGuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <UserGuestGuard userType={'user'}>
              <Register />
            </UserGuestGuard>
          )
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'magic-link', element: <MagicLink /> }
      ]
    },
    // LINGUIST
    {
      path: '/vendors',
      children: [
        {
          index: true,
          element: (
            <LinguistGuestGuard userType={'Linguist'}>
              <LinguistLogin />
            </LinguistGuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <LinguistGuestGuard userType={'Linguist'}>
              <LinguistRegister />
            </LinguistGuestGuard>
          )
        },
        { path: 'login-unprotected', element: <LinguistLogin /> },
        { path: 'register-unprotected', element: <LinguistRegister /> },
        { path: 'reset-password', element: <LinguistResetPassword /> },
        { path: 'new-password', element: <LinguistNewPassword /> },
        { path: 'magic-link', element: <LinguistMagicLink /> }
      ]
    },
    // Client
    {
      path: 'clients',
      children: [
        {
          index: true,
          element: (
            <ClientGuestGuard userType={'Client'}>
              <ClientLogin />
            </ClientGuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <ClientGuestGuard userType={'Client'}>
              <ClientRegister />
            </ClientGuestGuard>
          )
        },
        // { path: 'login', element: <ClientLogin /> },
        { path: 'register', element: <ClientRegister /> },
        { path: 'reset-password', element: <ClientResetPassword /> },
        { path: 'new-password', element: <ClientNewPassword /> },
        { path: 'magic-link', element: <ClientMagicLink /> }
      ]
    },

    // Dashboard Project manager Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard userType={['user']}>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'notifications/:id', element: <Suspense fallback={<LoadingScreen />}><NotificationOpen /></Suspense> },
        {
          path: 'app',
          element: (
            <AuthGuard userType={['user']}>
              {user?.role === 'Project Manager' ? (
                <GeneralProjectApp />
              ) : user?.role === 'Software Developer' ? (
                // A contained landing: no doors into the pages the role's
                // navigation never offers (see SoftwareDeveloperApp).
                <SoftwareDeveloperApp />
              ) : (
                <GeneralQuoteApp />
              )}
            </AuthGuard>
          )
        },
        {
          path: 'user',
          children: [
            {
              element: <Navigate to="/dashboard/user/profile" replace />,
              index: true
            },
            { path: 'cards', element: <UserCards /> },
            { path: 'new', element: <UserCreate /> },
            { path: ':id/edit', element: <UserEdit /> },
            { path: 'account', element: <UserAccount /> },
            { path: 'report/:id', element: <AMReport /> },
            { path: 'report/quote/:id', element: <AMReport type={'quote'} /> },
            {
              path: 'report/clients/:id',
              element: <AMReport type={'clients'} />
            },
            {
              path: 'report/activity/:id',
              element: <AMReport type={'activity'} />
            }
          ]
        },

        {
          path: 'task',
          children: [
            {
              element: <Navigate to="/dashboard/task/list" replace />,
              index: true
            },
            { path: 'list/:dept', element: <TaskList /> },
            { path: 'list', element: <TasksDepartmentsList /> },
            { path: 'new', element: <TaskCreate /> },
            { path: ':id/edit', element: <TaskEdit /> }
          ]
        },
        { path: 'bits-and-bobs', element: <BitsAndBobs /> },

        {
          path: 'vendor',
          children: [
            {
              element: <Navigate to="/dashboard/vendor/translator" replace />,
              index: true
            },
            { path: 'translator', element: <LinguistTranslatorList /> },
            { path: 'interpreter', element: <LinguistInterpreterList /> },
            { path: 'mediator', element: <LinguistMediatorList /> },
            { path: 'hostess', element: <LinguistHostessList /> },
            { path: 'mediatorBilling', element: <MediatorBillingApprovals /> },
            { path: 'incomplete', element: <LinguistIncompleteList /> },
            { path: 'technician', element: <LinguistTechnicianList /> },
            { path: 'technician/:id', element: <TechnicianRecord /> },
            { path: 'formatter', element: <LinguistFormatterList /> },
            { path: 'blacklist-vendors', element: <LinguistBlackList /> },
            { path: 'deleted', element: <LinguistDeletedList /> },
            { path: ':id', element: <LinguistDetail /> },
            { path: ':id/cv', element: <LinguistCVDetail /> },
            { path: 'formatter/:id', element: <FormatterView /> }
          ]
        },

        {
          path: 'TwilioCalls',
          children: [
            {
              element: <Navigate to="/dashboard/TwilioCalls/call" replace />,
              index: true
            },
            { path: 'call', element: <TwilioCall /> }
          ]
        },
        // {
        //   path: 'TextToSpeech',
        //   children: [
        //     { element: <Navigate to="/dashboard/TextToSpeech/create" replace />, index: true },
        //     { path: 'create', element: <TextToSpeech /> },
        //   ],
        // },

        {
          path: 'barzanoProject',
          children: [
            {
              element: <Navigate to="/dashboard/barzanoProject/list" replace />,
              index: true
            },
            { path: 'list', element: <BarzanoProjectList /> },
            { path: 'deliverylist', element: <BarzanoDeliveryProjectList /> },
            { path: 'new', element: <BarzanoProjectCreate /> },
            { path: ':id', element: <BarzanoProjectDetails /> },
            { path: ':id/booking', element: <BarzanoProjectBooking /> },
            { path: ':id/edit', element: <BarzanoProjectEdit /> },
            { path: 'ocr', element: <OcrReport /> }
          ]
        },
        {
          path: 'bugnionProject',
          children: [
            {
              element: <Navigate to="/dashboard/bugnionProject/list" replace />,
              index: true
            },
            {
              path: 'list',
              element: <BugnionProjectList />
            },
            {
              path: 'deliverylist',
              element: <BugnionDeliveryProjectList />
            },
            {
              path: 'new',
              element: <BugnionProjectCreate />
            },
            {
              path: ':id',
              element: <BugnionProjectDetails />
            },
            {
              path: ':id/booking',
              element: <BugnionProjectBooking />
            },
            {
              path: ':id/edit',
              element: <BugnionProjectEdit />
            },
            {
              path: 'ocr',
              element: <OcrReport />
            }
          ]
        },
        {
          path: 'translationProject',
          children: [
            {
              element: (
                <Navigate to="/dashboard/translationProject/list" replace />
              ),
              index: true
            },
            { path: 'list', element: <TranslationProjectList /> },
            { path: 'new', element: <TranslationProjectCreate /> },
            { path: ':id', element: <TranslationProjectDetails /> },
            { path: ':id/booking', element: <TranslationProjectBooking /> },
            { path: ':id/edit', element: <TranslationProjectEdit /> }
          ]
        },
        {
          path: 'company',
          children: [
            {
              element: <Navigate to="/dashboard/company" replace />,
              index: true
            },
            { path: 'portfolio', element: <Portfolio /> },
            { path: 'my-opportunities', element: <Opportunities /> },
            { path: 'list', element: <CompanyList /> },
            { path: 'new', element: <CompanyCreate /> },
            { path: ':id', element: <CompanyDetails /> },
            { path: ':id/edit', element: <CompanyEdit /> }
          ]
        },
        {
          path: 'interpretationProject',
          children: [
            {
              element: (
                <Navigate to="/dashboard/interpretationProject/list" replace />
              ),
              index: true
            },
            { path: 'list', element: <InterpretationProjectList /> },
            { path: 'new', element: <InterpretationProjectCreate /> },
            { path: ':id', element: <InterpretationProjectDetails /> },
            { path: ':id/booking', element: <InterpretationProjectBooking /> },
            { path: ':id/edit', element: <InterpretationProjectEdit /> }
          ]
        },
        {
          path: 'mediationProject',
          children: [
            {
              element: (
                <Navigate to="/dashboard/mediationProject/list" replace />
              ),
              index: true
            },
            { path: 'list', element: <MediationProjectList /> },
            { path: 'new', element: <MediationProjectCreate /> },
            { path: ':id', element: <MediationProjectDetails /> },
            { path: ':id/booking', element: <MediationProjectBooking /> },
            { path: ':id/edit', element: <MediationProjectEdit /> }
          ]
        },
        {
          path: 'academyProject',
          children: [
            {
              element: (
                <Navigate to="/dashboard/academyProject/list" replace />
              ),
              index: true
            },
            { path: 'list', element: <AcademyProjectList /> },
            { path: 'new', element: <AcademyProjectCreate /> },
            { path: ':id', element: <AcademyProjectDetails /> },
            { path: ':id/edit', element: <AcademyProjectEdit /> }
          ]
        },
        {
          path: 'softwaredevProject',
          children: [
            {
              element: (
                <Navigate to="/dashboard/softwaredevProject/list" replace />
              ),
              index: true
            },
            { path: 'list', element: <SoftwareDevProjectList /> },
            { path: 'new', element: <SoftwareDevProjectCreate /> },
            { path: ':id', element: <SoftwareDevProjectDetails /> },
            { path: ':id/edit', element: <SoftwareDevProjectEdit /> }
          ]
        },
        {
          path: 'videoConferenceProject',
          children: [
            {
              element: (
                <Navigate to="/dashboard/videoConferenceProject/list" replace />
              ),
              index: true
            },
            { path: 'list', element: <VideoConferenceProjectList /> },
            { path: 'new', element: <VideoConferenceProjectCreate /> },
            { path: ':id', element: <VideoConferenceProjectDetails /> },
            { path: ':id/edit', element: <VideoConferenceProjectEdit /> }
          ]
        },
        { path: 'calendar', element: <Calendar /> },
        { path: 'projects/calendar', element: <Calendar /> },
        { path: 'vault', element: <Vault /> },
        { path: 'vault/:id', element: <VaultFolder /> },
        { path: 'vault/:id/:fileType', element: <VaultFiles /> },
        // THE BLOG. The list, the form and the edit page are gated to admin
        // and Software Developer inside each page (RoleBasedGuard); a post's
        // page itself is open to whoever it was written for, and the server
        // refuses a software-team post to anyone else.
        {
          path: 'blog',
          children: [
            {
              element: <Navigate to="/dashboard/blog/posts" replace />,
              index: true
            },
            { path: 'posts', element: <BlogPosts /> },
            { path: 'new', element: <CreatePost /> },
            { path: 'post/:id', element: <BlogPost /> },
            { path: 'post/:id/edit', element: <EditPost /> }
          ]
        },
        // The address the form used to live at, kept for bookmarks.
        {
          path: 'create-post',
          element: <Navigate to="/dashboard/blog/new" replace />
        },

        {
          path: 'issue',
          children: [
            {
              element: <Navigate to="/dashboard/issue/list" replace />,
              index: true
            },
            { path: 'list', element: <IssueList /> },
            { path: 'new', element: <IssueCreate /> },
            { path: ':id', element: <IssueDetails /> },
            { path: ':id/edit', element: <IssueEdit /> }
          ]
        },
        {
          path: 'golden-pin',
          children: [
            {
              element: <Navigate to="/dashboard/golden-pin/list" replace />,
              index: true
            },
            { path: 'list', element: <GoldenPinList /> },
            { path: 'new', element: <GoldenPinCreate /> },
            { path: ':id/edit', element: <GoldenPinEdit /> },
            { path: ':id', element: <GoldenPinView /> }
          ]
        },
        {
          path: 'invoice',
          children: [
            {
              element: <Navigate to="/dashboard/invoice/list" replace />,
              index: true
            },
            { path: 'barzanoClient/create', element: <BarzanoInvoiceCreate /> },
            { path: 'client/create', element: <LYCIClientInvoiceCreate /> },
            { path: 'client/:id/edit', element: <BarzanoInvoiceEdit /> },
            { path: 'create', element: <InvoiceCreate /> },
            // { path: 'client/:id/edit', element: <InvoiceEdit /> },
            { path: 'client/:id', element: <BarzanoInvoiceView /> },
            { path: 'client/list', element: <BarzanoInvoiceList /> },
            { path: 'list', element: <InvoiceList /> },
            { path: 'financeBilling', element: <FinanceBillingList /> },
            { path: ':id', element: <InvoiceDetails /> }
            // { path: ':id/editFZ', element: <IssueEdit /> },
          ]
        },
        {
          path: 'reports',
          children: [
            {
              element: <Navigate to="/dashboard/reports/quote" replace />,
              index: true
            },
            { path: 'quote', element: <QuotesConversionReports /> },
            {
              path: 'marketingChannelList',
              element: <MarketingChannelQuotesList />
            },
            { path: 'calls', element: <CallRecordsList /> },
            { path: 'private-clients', element: <PrivateClientsReport /> },
            { path: 'issue', element: <IssueReport /> },
            { path: 'totalsales', element: <TotalSalesFlowChartReport /> },
            { path: 'credit-control', element: <ClientFlowChartReport /> }
            //  { path: 'vendors/cashflows', element: <VendorCashFlowReports />}
          ]
        },
        { path: 'management', element: <Management /> },
        // the financial seal page: by its link only, not in the sidebar
        { path: 'financial-seal', element: <FinancialSealControl /> },
        { path: 'permission-denied', element: <PermissionDenied /> },
        { path: 'communications', element: <EmailConversations /> },
        {
          path: 'settings',
          children: [
            {
              path: 'communication',
              children: [
                {
                  path: 'email-template/list',
                  element: <EmailTemplateList />
                },
                {
                  path: 'email-template/new',
                  element: <EmailTemplateCreate />
                },
                {
                  path: 'email-template/:id/edit',
                  element: <EmailTemplateEdit />
                },
                {
                  path: 'email-template/:id',
                  element: <EmailTemplateView />
                },
                { path: 'mailboxes', element: <EmailMailboxAdmin /> }
              ]
            },
            {
              path: 'kpi',
              children: [
                { path: 'list', element: <KPIList /> },
                { path: 'new', element: <KPICreate /> },
                { path: ':id/edit', element: <KPICreate /> }
              ]
            },
            {
              path: 'service/list',
              element: <ServicesMgtList />
            },
            {
              path: 'service/view/:id',
              element: <ServiceViewPage />
            }
          ]
        }
      ]
    },
    // Dashboard Account Manager Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard userType={['user']}>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        {
          path: 'app',
          element: (
            <AuthGuard userType={['user']}>
              {user?.role === 'Project Manager' ? (
                <GeneralProjectApp />
              ) : user?.role === 'Software Developer' ? (
                // A contained landing: no doors into the pages the role's
                // navigation never offers (see SoftwareDeveloperApp).
                <SoftwareDeveloperApp />
              ) : (
                <GeneralQuoteApp />
              )}
            </AuthGuard>
          )
        },
        {
          path: 'user',
          children: [
            {
              element: <Navigate to="/dashboard/user/profile" replace />,
              index: true
            },
            { path: 'cards', element: <UserCards /> },
            { path: 'new', element: <UserCreate /> },
            { path: ':id/edit', element: <UserEdit /> },
            { path: 'account', element: <UserAccount /> }
          ]
        },

        {
          path: 'task',
          children: [
            {
              element: <Navigate to="/dashboard/task/list" replace />,
              index: true
            },
            { path: 'list/:dept', element: <TaskList /> },
            { path: 'list', element: <TasksDepartmentsList /> },
            { path: 'new', element: <TaskCreate /> },
            { path: ':id/edit', element: <TaskEdit /> }
          ]
        },
        { path: 'bits-and-bobs', element: <BitsAndBobs /> },

        {
          path: 'linguist',
          children: [
            {
              element: <Navigate to="/dashboard/linguist/list" replace />,
              index: true
            }
            // { path: 'list', element: <LinguitList /> },
          ]
        },

        {
          path: 'TwilioCalls',
          children: [
            {
              element: <Navigate to="/dashboard/TwilioCalls/call" replace />,
              index: true
            },
            { path: 'call', element: <TwilioCall /> }
          ]
        },
        // {
        //   path: 'TextToSpeech',
        //   children: [
        //     { element: <Navigate to="/dashboard/TextToSpeech/create" replace />, index: true },
        //     { path: 'create', element: <TextToSpeech /> },
        //   ],
        // },
        {
          path: 'translationQuote',
          children: [
            {
              element: (
                <Navigate to="/dashboard/translationQuote/list" replace />
              ),
              index: true
            },
            { path: 'list', element: <TranslationQuoteList /> },
            { path: 'mine', element: <QuotesByOwner kind="translation" /> },
            {
              path: 'am/:name',
              element: <QuotesByOwner kind="translation" />
            },
            { path: 'new', element: <TranslationQuoteCreate /> },
            { path: ':id', element: <TranslationQuoteDetails /> },
            { path: ':id/edit', element: <TranslationQuoteEdit /> }
          ]
        },
        {
          path: 'company',
          children: [
            {
              element: <Navigate to="/dashboard/company/list" replace />,
              index: true
            },
            { path: 'list', element: <CompanyList /> },
            { path: 'new', element: <CompanyCreate /> },
            { path: ':id', element: <CompanyDetails /> },
            { path: ':id/edit', element: <CompanyEdit /> }
          ]
        },
        {
          path: 'interpretationQuote',
          children: [
            {
              element: (
                <Navigate to="/dashboard/interpretationQuote/list" replace />
              ),
              index: true
            },
            { path: 'list', element: <InterpretationQuoteList /> },
            { path: 'mine', element: <QuotesByOwner kind="interpretation" /> },
            {
              path: 'am/:name',
              element: <QuotesByOwner kind="interpretation" />
            },
            { path: 'new', element: <InterpretationQuoteCreate /> },
            { path: ':id', element: <InterpretationQuoteDetails /> },
            { path: ':id/edit', element: <InterpretationQuoteEdit /> }
          ]
        },
        {
          path: 'academyQuote',
          children: [
            {
              element: (
                <Navigate to="/dashboard/academyQuote/list" replace />
              ),
              index: true
            },
            { path: 'list', element: <AcademyQuoteList /> },
            { path: 'new', element: <AcademyQuoteCreate /> },
            { path: ':id', element: <AcademyQuoteDetails /> },
            { path: ':id/edit', element: <AcademyQuoteEdit /> }
          ]
        },
        {
          path: 'softwaredevQuote',
          children: [
            {
              element: (
                <Navigate to="/dashboard/softwaredevQuote/list" replace />
              ),
              index: true
            },
            { path: 'list', element: <SoftwareDevQuoteList /> },
            { path: 'new', element: <SoftwareDevQuoteCreate /> },
            { path: ':id', element: <SoftwareDevQuoteDetails /> },
            { path: ':id/edit', element: <SoftwareDevQuoteEdit /> }
          ]
        },
        {
          path: 'videoConferenceProject',
          children: [
            {
              element: (
                <Navigate to="/dashboard/videoConferenceProject/list" replace />
              ),
              index: true
            },
            { path: 'list', element: <VideoConferenceProjectList /> },
            { path: 'new', element: <VideoConferenceProjectCreate /> },
            { path: ':id', element: <VideoConferenceProjectDetails /> },
            { path: ':id/edit', element: <VideoConferenceProjectEdit /> }
          ]
        },

        {
          path: 'reports',
          children: [
            {
              element: <Navigate to="/dashboard/reports/quote" replace />,
              index: true
            },
            { path: 'quote', element: <QuotesConversionReports /> },
            {
              path: 'marketingChannelList',
              element: <MarketingChannelQuotesList />
            },
            { path: 'calls', element: <CallRecordsList /> },
            { path: 'private-clients', element: <PrivateClientsReport /> },
            { path: 'issue', element: <IssueReport /> },
            { path: 'allProjectsList', element: <AllGeneralProjectsList /> },
            // one page for both patent firms since 09/10/2026; the two old
            // addresses keep working for the links people have sent
            { path: 'patents', element: <PatentReports /> },
            {
              path: 'barzano',
              element: <Navigate to="/dashboard/reports/patents?firm=barzano" replace />
            },
            {
              path: 'bugnion',
              element: <Navigate to="/dashboard/reports/patents?firm=bugnion" replace />
            },
            { path: 'revenueAnalysis', element: <TotalSalesFlowChartReport /> },
            { path: 'expenseReport', element: <ExpenseReport /> },
            { path: 'treasuryChart', element: <TreasuryChart /> },
            { path: 'costReport', element: <CostReport /> },
            { path: 'marketing-roi-analysis-report', element: <ROIAnalysis /> },
            {
              path: 'am-reports',
              element: <AMReports />
            },
            {
              path: 'pm-reports',
              element: <PMReports />
            },

            { path: 'goldenPinReports', element: <GoldenPinReports /> }
          ]
        },
        {
          path: 'brochure',
          children: [
            {
              element: <Navigate to="/dashboard/brochure/list" replace />,
              index: true
            },
            { path: 'list', element: <BrochureList /> },
            { path: 'new', element: <BrochureCreate /> },
            { path: 'ai', element: <BrochureAiList /> },
            { path: 'ai/:id', element: <BrochureAiDetail /> },
            { path: ':id/edit', element: <BrochureEdit /> },
            { path: ':id', element: <BrochureDetails /> }
          ]
        },
        {
          path: 'expense',
          children: [
            {
              element: <Navigate to="/dashboard/expense/list" replace />,
              index: true
            },
            { path: 'list', element: <ExpenseList /> },
            { path: 'new', element: <ExpenseCreate /> },
            { path: ':id/edit', element: <ExpenseEdit /> },
            { path: ':id', element: <ExpenseDetails /> }
          ]
        },
        {
          path: 'budget',
          children: [
            {
              element: <Navigate to="/dashboard/budget/list" replace />,
              index: true
            },
            { path: 'list', element: <BudgetList /> },
            {
              path: 'new',
              element: <BudgetCreate />
            },
            { path: ':id/edit', element: <BudgetCreate /> }
          ]
        },
        {
          path: 'credit-note',
          children: [
            {
              element: <Navigate to="/dashboard/expense/list" replace />,
              index: true
            },
            { path: 'new', element: <CreditNoteCreate /> },
            { path: ':id/edit', element: <CreditNoteEdit /> },
            { path: ':id', element: <CreditNoteDetails /> }
          ]
        },
        {
          path: 'inventory',
          children: [
            {
              element: <Navigate to="/dashboard/inventory/list" replace />,
              index: true
            },
            { path: 'list', element: <InventoryList /> },
            { path: 'new', element: <InventoryCreate /> },
            { path: ':id/edit', element: <InventoryEdit /> },
            { path: ':id', element: <InventoryDetails /> },
            { path: 'overview', element: <InventoryOverview /> }
          ]
        },
        {
          path: 'requests',
          children: [
            {
              element: <Navigate to="/dashboard/requests/list" replace />,
              index: true
            },
            { path: 'list', element: <RequestList /> },
            { path: ':id', element: <RequestsDetailPage /> }
          ]
        },
        {
          path: 'ticket',
          children: [
            {
              element: <Navigate to="/dashboard/ticket/list" replace />,
              index: true
            },
            { path: 'list', element: <TicketList /> },
            { path: 'new', element: <TicketCreate /> },
            { path: ':id/edit', element: <TicketEdit /> },
            { path: ':id', element: <TicketDetails /> },
            { path: 'kanban', element: <TicketKanban /> },
            { path: 'support', element: <TicketSupport /> }
          ]
        },

        { path: 'calendar', element: <Calendar /> },
        { path: 'projects/calendar', element: <Calendar /> },

        { path: 'permission-denied', element: <PermissionDenied /> }
      ]
    },
    {
      path: 'vendors',
      element: (
        <AuthGuard userType={['Marketeer']}>
          <VendorLayout />
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={PATH_AFTER_LOGIN_LINGUIST} replace />,
          index: true
        },
        { path: 'marketingChannelList', element: <MarketingChannelList /> }
      ]
    },
    // Formatter
    {
      path: 'vendors',
      element: (
        <AuthGuard userType={['Formatter']}>
          <VendorLayout />
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={PATH_AFTER_LOGIN_LINGUIST} replace />,
          index: true
        },
        { path: 'formattingProject/:id', element: <FormattingFileDetails /> },
        { path: 'formattingProjectslist', element: <FormattingFilesList /> },
        { path: 'projectsCalender', element: <ProjectsCalendar /> }
      ]
    },
    // Technician
    {
      path: 'vendors',
      element: (
        <AuthGuard userType={['Technician']}>
          <VendorLayout />
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={PATH_AFTER_LOGIN_LINGUIST} replace />,
          index: true
        },
        { path: 'technicianSheets/:id', element: <TechnicianDetails /> },
        { path: 'technicianSheets', element: <TechnicianSheets /> },
        { path: 'inventory', element: <TechnicianInventoryList /> },
        { path: 'inventory/:id', element: <TechnicianInventoryDetails /> },
        { path: 'inventory/new', element: <TechnicianInventoryCreate /> },
        { path: 'inventory/:id/edit', element: <TechnicianInventoryEdit /> }
      ]
    },
    // Linguist
    {
      path: 'vendors',
      element: (
        <AuthGuard userType={['Linguist']}>
          <VendorProfileOnBoarding />
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={PATH_AFTER_LOGIN_LINGUIST} replace />,
          index: true
        },
        { path: 'invoices', element: <VendorInvoices /> },
        { path: 'invoices/:id', element: <VendorInvoiceDetail /> },
        { path: 'invoices/new', element: <VendorInvoiceCreate /> },
        {
          path: 'translationAllocationResponse',
          children: [
            { element: <Navigate to="/404" replace />, index: true },
            { path: ':id', element: <TranslationAllocationResponse /> }
          ]
        },
        {
          path: 'barzanoAllocationResponse',
          children: [
            { element: <Navigate to="/404" replace />, index: true },
            { path: ':id', element: <BarzanoAllocationResponse /> }
          ]
        },
        {
          path: 'interpretationAllocationResponse',
          children: [
            { element: <Navigate to="/404" replace />, index: true },
            { path: ':id', element: <InterpretationAllocationResponse /> }
          ]
        },

        { path: 'dashboard', element: <LinguistDashboard /> },
        { path: 'onBoardingSteps', element: <></> },
        { path: 'profile', element: <LinguistProfile /> },
        { path: 'projects', element: <LinguistProjects /> },
        { path: 'paymentInfo', element: <LinguistPaymentInfo /> },
        {
          path: 'services',
          element: <Services />
        },
        { path: 'invoice', element: <VendorInvoices /> },
        { path: 'invoice/:id', element: <VendorInvoiceDetail /> },
        { path: 'invoice/new', element: <VendorInvoiceCreate /> },
        { path: 'translationProjects/list', element: <TranslationProjects /> },
        { path: 'translationProjects/:allocationId', element: <ProjectView /> },
        {
          path: 'interpretationProjects/list',
          element: <InterpretationProjects />
        },
        {
          path: 'interpretationProjects/:allocationId',
          element: <ProjectView />
        },
        // Mediazione: il canale dipende dall'ente, non dal tipo di progetto
        // (report ASST Lariana 15/09/2026). Chi lavora su AG Mediators viene
        // intercettato da MediationRedirectGate prima che la vista si monti;
        // chi chiude il ciclo su Universe — ASST Lariana — vede qui il
        // dettaglio dell'incarico, come un interprete.
        {
          path: 'mediationProjects/list',
          element: <MediatorPortalRedirect />
        },
        {
          path: 'mediationProjects/:allocationId',
          element: <ProjectView />
        },
        { path: 'verify-email', element: <VerifyEmail /> },
        { path: 'permission-denied', element: <VendorPermissionDenied /> },
        { path: 'change-password', element: <UpdatePassword /> },

        { path: 'invoice', element: <VendorInvoices /> },
        { path: 'invoice/:id', element: <VendorInvoiceDetail /> },
        { path: 'invoice/new', element: <VendorInvoiceCreate /> },
        { path: 'translationProjects/list', element: <TranslationProjects /> },
        { path: 'translationProjects/:allocationId', element: <ProjectView /> },
        {
          path: 'interpretationProjects/list',
          element: <InterpretationProjects />
        },
        {
          path: 'interpretationProjects/:allocationId',
          element: <ProjectView />
        },
        // Mediazione: il canale dipende dall'ente, non dal tipo di progetto
        // (report ASST Lariana 15/09/2026). Chi lavora su AG Mediators viene
        // intercettato da MediationRedirectGate prima che la vista si monti;
        // chi chiude il ciclo su Universe — ASST Lariana — vede qui il
        // dettaglio dell'incarico, come un interprete.
        {
          path: 'mediationProjects/list',
          element: <MediatorPortalRedirect />
        },
        {
          path: 'mediationProjects/:allocationId',
          element: <ProjectView />
        },
        { path: 'verify-email', element: <VerifyEmail /> },
        { path: 'permission-denied', element: <PermissionDenied /> },
        { path: 'change-password', element: <UpdatePassword /> },
        {
          path: 'pre-booking',
          children: [
            { element: <Navigate to="/404" replace />, index: true },
            { path: ':id', element: <PreBookingResponse /> }
          ]
        },
        {
          path: 'booking',
          children: [
            { element: <Navigate to="/404" replace />, index: true },
            { path: ':id', element: <BookingResponse /> }
          ]
        }
      ]
    },
    // {
    //   path: 'client',
    //   element: (
    //     <AuthGuard userType={['Client']}>
    //       <LogoOnlyLayout />
    //     </AuthGuard>
    //   ),
    //   children: [
    //     // { element: <Navigate to={PATH_AFTER_LOGIN_CLIENT} replace />, index: true },
    //     { path: 'barzanoCalendar', element: <BarzanoCalendar /> }
    //   ]
    // },
    // Client
    {
      path: 'clients',
      element: (
        <AuthGuard userType={['Client']}>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        // { element: <Navigate to={PATH_AFTER_LOGIN_CLIENT} replace />, index: true },
        { path: 'dashboard', element: <Dashboard /> },
        // { path: 'barzanoCalendar', element: <BarzanoCalendar /> },
        { path: 'edit-profile', element: <EditClient /> },
        { path: 'instant-quote', element: <InstantQuote /> },
        { path: 'order', element: <OrderList /> },
        { path: 'order/:type/:id', element: <OrderDetails /> },
        { path: 'book/interpreter', element: <BookInterpreter /> },
        { path: 'interpreter-payment/:id', element: <InterpreterPayment /> },
        { path: 'interpreter-order/:id', element: <InterpreterOrderNow /> },
        { path: 'get-interpret-quote/:id', element: <InterpreterGetQuote /> },
        { path: 'book/web-conference', element: <BookWebConference /> },
        {
          path: 'webconference-payment/:id',
          element: <WebConferencePayment />
        },
        { path: 'webconference-order/:id', element: <WebConferenceOrderNow /> },
        { path: 'webconference-quote/:id', element: <WebConferenceGetQuote /> },
        { path: 'book/translator', element: <BookTranslator /> },
        { path: 'translator-payment/:id', element: <TranslatorPayment /> },
        { path: 'translator-order/:id', element: <TranslatorOrderNow /> },
        { path: 'translator-quote/:id', element: <TranslatorGetQuote /> }
      ]
    },

    // Main Routes
    {
      path: '/',
      element: <LogoOnlyLayout />,
      children: [
        { element: <HomePage />, index: true },
        { path: 'comingsoon', element: <ComingSoon />, index: true },
        { path: '404', element: <Page404 /> },
        // { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    // Rimando all'incarico sul Portale Mediatori: PUBBLICA di proposito, fuori
    // da AuthGuard. È la destinazione di ripiego delle email di mediazione
    // quando l'indirizzo del portale non è ancora noto al build del client —
    // il mediatore non ha credenziali Universe e non deve incontrare una login.
    { path: '/incarico/:allocationId', element: <MediatorPortalRedirect /> },
    { path: '/vendors/terms&conditions', element: <TermsAndConditions /> },
    { path: '/vendors/travel-policy', element: <TravelPolicy /> },
    { path: '/vendors/privacypolicy', element: <PrivacyPolicy /> },
    { path: '/files/:id/:fileType', element: <TranslatedFiles /> },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

// AUTHENTICATION
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(
  lazy(() => import('../pages/auth/ResetPassword'))
);
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const MagicLink = Loadable(lazy(() => import('../pages/auth/MagicLink')));

// LINGUIST AUTHENTICATION
const LinguistRegister = Loadable(
  lazy(() => import('../pages/auth/linguistAuth/Register'))
);
const LinguistResetPassword = Loadable(
  lazy(() => import('../pages/auth/linguistAuth/ResetPassword'))
);
const LinguistNewPassword = Loadable(
  lazy(() => import('../pages/auth/linguistAuth/NewPassword'))
);
const LinguistMagicLink = Loadable(
  lazy(() => import('../pages/auth/linguistAuth/MagicLink'))
);

// Client AUTHENTICATION
const ClientRegister = Loadable(
  lazy(() => import('../pages/auth/clientAuth/Register'))
);
const ClientResetPassword = Loadable(
  lazy(() => import('../pages/auth/clientAuth/ResetPassword'))
);
const ClientNewPassword = Loadable(
  lazy(() => import('../pages/auth/clientAuth/NewPassword'))
);
const ClientMagicLink = Loadable(
  lazy(() => import('../pages/auth/clientAuth/MagicLink'))
);

// DASHBOARD

// GENERAL
const AllGeneralProjectsList = Loadable(
  lazy(() => import('../pages/dashboard/AllGeneralProjectsList'))
);
const FinanceBillingList = Loadable(
  lazy(() => import('../pages/dashboard/FinanceBillingList'))
);

const GeneralQuoteApp = Loadable(
  lazy(() => import('../pages/dashboard/GeneralQuoteApp'))
);
const GeneralProjectApp = Loadable(
  lazy(() => import('../pages/dashboard/GeneralApp'))
);
const SoftwareDeveloperApp = Loadable(
  lazy(() => import('../pages/dashboard/SoftwareDeveloperApp'))
);
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
const Vault = Loadable(lazy(() => import('../pages/dashboard/Vault')));
const VaultFolder = Loadable(
  lazy(() => import('../pages/dashboard/VaultFolder'))
);
const VaultFiles = Loadable(
  lazy(() => import('../pages/dashboard/VaultFiles'))
);
const CreatePost = Loadable(
  lazy(() => import('../pages/dashboard/CreatePost'))
);
const EditPost = Loadable(lazy(() => import('../pages/dashboard/EditPost')));
const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
const IssueCreate = Loadable(
  lazy(() => import('../pages/dashboard/IssueCreate'))
);
const IssueEdit = Loadable(lazy(() => import('../pages/dashboard/IssueEdit')));
const IssueList = Loadable(lazy(() => import('../pages/dashboard/IssueList')));
const IssueDetails = Loadable(
  lazy(() => import('../pages/dashboard/IssueDetails'))
);
const IssueReport = Loadable(
  lazy(() => import('../pages/dashboard/IssueReport'))
);
const InvoiceList = Loadable(
  lazy(() => import('../pages/dashboard/InvoiceList'))
);
const InvoiceDetails = Loadable(
  lazy(() => import('../pages/dashboard/InvoiceDetails'))
);
const GoldenPinList = Loadable(
  lazy(() => import('../pages/dashboard/GoldenPinList'))
);
const GoldenPinCreate = Loadable(
  lazy(() => import('../pages/dashboard/GoldenPinCreate'))
);
const GoldenPinEdit = Loadable(
  lazy(() => import('../pages/dashboard/GoldenPinEdit'))
);
const GoldenPinView = Loadable(
  lazy(() => import('../pages/dashboard/GoldenPinView'))
);
const GoldenPinReports = Loadable(
  lazy(() => import('../pages/dashboard/GoldenPinsReport'))
);
// TASK
const TaskList = Loadable(lazy(() => import('../pages/dashboard/TaskList')));
const TaskCreate = Loadable(
  lazy(() => import('../pages/dashboard/TaskCreate'))
);
const TaskEdit = Loadable(lazy(() => import('../pages/dashboard/TaskEdit')));
const TasksDepartmentsList = Loadable(
  lazy(() => import('../pages/dashboard/TasksDepartmentsList'))
);
const BitsAndBobs = Loadable(
  lazy(() => import('../pages/dashboard/BitsAndBobs'))
);
// LINGUIT
const LinguistTranslatorList = Loadable(
  lazy(() => import('../pages/dashboard/LinguistTranslatorList'))
);
const LinguistInterpreterList = Loadable(
  lazy(() => import('../pages/dashboard/LinguistInterpreterList'))
);
const LinguistMediatorList = Loadable(
  lazy(() => import('../pages/dashboard/LinguistMediatorList'))
);
const LinguistHostessList = Loadable(
  lazy(() => import('../pages/dashboard/LinguistHostessList'))
);
const MediatorBillingApprovals = Loadable(
  lazy(() => import('../pages/dashboard/MediatorBillingApprovals'))
);
const LinguistIncompleteList = Loadable(
  lazy(() => import('../pages/dashboard/LinguistIncompleteList'))
);
const LinguistTechnicianList = Loadable(
  lazy(() => import('../pages/dashboard/LinguistTechnicianList'))
);
const TechnicianRecord = Loadable(
  lazy(() => import('../pages/dashboard/TechnicianRecord'))
);
const LinguistFormatterList = Loadable(
  lazy(() => import('../pages/dashboard/LinguistFormatterList'))
);
const LinguistBlackList = Loadable(
  lazy(() => import('../pages/dashboard/LinguistBlackList'))
);
const LinguistDeletedList = Loadable(
  lazy(() => import('../pages/dashboard/LinguistDeletedList'))
);
const LinguistDetail = Loadable(
  lazy(() => import('../pages/dashboard/LinguistDetail'))
);
const LinguistCVDetail = Loadable(
  lazy(() => import('../pages/dashboard/LinguistCVDetails'))
);
const FormatterView = Loadable(
  lazy(() => import('../pages/dashboard/FormatterDetails'))
);
const VendorProfileOnBoarding = Loadable(
  lazy(() => import('../pages/linguist/ProfileOnBoarding'))
);
const VendorInvoices = Loadable(
  lazy(() => import('../pages/linguist/InvoiceList'))
);
const VendorInvoiceDetail = Loadable(
  lazy(() => import('../pages/linguist/InvoiceDetails'))
);
const VendorInvoiceCreate = Loadable(
  lazy(() => import('../pages/linguist/InvoiceCreate'))
);
// BARZANO PROJECT
const BarzanoDeliveryProjectList = Loadable(
  lazy(() => import('../pages/dashboard/BarzanoDeliveryProjectsList'))
);
const BarzanoProjectList = Loadable(
  lazy(() => import('../pages/dashboard/BarzanoProjectList'))
);
const BarzanoProjectCreate = Loadable(
  lazy(() => import('../pages/dashboard/BarzanoProjectCreate'))
);
const BarzanoProjectEdit = Loadable(
  lazy(() => import('../pages/dashboard/BarzanoProjectEdit'))
);
const BarzanoProjectDetails = Loadable(
  lazy(() => import('../pages/dashboard/BarzanoProjectDetails'))
);
const BarzanoProjectBooking = Loadable(
  lazy(() => import('../pages/dashboard/BarzanoProjectBooking'))
);

// BUGNION PROJECT (placeholders)
const BugnionDeliveryProjectList = Loadable(
  lazy(() => import('../pages/dashboard/BugnionDeliveryProjectsList'))
);
const BugnionProjectList = Loadable(
  lazy(() => import('../pages/dashboard/BugnionProjectList'))
);
const BugnionProjectCreate = Loadable(
  lazy(() => import('../pages/dashboard/BugnionProjectCreate'))
);
const BugnionProjectEdit = Loadable(
  lazy(() => import('../pages/dashboard/BugnionProjectEdit'))
);
const BugnionProjectDetails = Loadable(
  lazy(() => import('../pages/dashboard/BugnionProjectDetails'))
);
const BugnionProjectBooking = Loadable(
  lazy(() => import('../pages/dashboard/BugnionProjectBooking'))
);

// TRANSLATION PROJECT
const TranslationProjectCreate = Loadable(
  lazy(() => import('../pages/dashboard/TranslationProjectCreate'))
);
const TranslationProjectList = Loadable(
  lazy(() => import('../pages/dashboard/TranslationProjectList'))
);
const TranslationProjectEdit = Loadable(
  lazy(() => import('../pages/dashboard/TranslationProjectEdit'))
);
const TranslationProjectDetails = Loadable(
  lazy(() => import('../pages/dashboard/TranslationProjectDetails'))
);
const TranslationProjectBooking = Loadable(
  lazy(() => import('../pages/dashboard/TranslationProjectBooking'))
);

//TRANSLATION QUOTE
const TranslationQuoteCreate = Loadable(
  lazy(() => import('../pages/dashboard/TranslationQuoteCreate'))
);
const TranslationQuoteList = Loadable(
  lazy(() => import('../pages/dashboard/TranslationQuoteList'))
);
const QuotesByOwner = Loadable(
  lazy(() => import('../pages/dashboard/QuotesByOwner'))
);
const TranslationQuoteEdit = Loadable(
  lazy(() => import('../pages/dashboard/TranslationQuoteEdit'))
);
const TranslationQuoteDetails = Loadable(
  lazy(() => import('../pages/dashboard/TranslationQuoteDetails'))
);
// const TranslationQuotesReports = Loadable(lazy(() => import('../pages/dashboard/TranslationQuotesReports')));

// REPORTS
const TotalSalesFlowChartReport = Loadable(
  lazy(() => import('../pages/dashboard/TotalSalesFlowChartReport'))
);
const ClientFlowChartReport = Loadable(
  lazy(() => import('../pages/dashboard/ClientFlowChartReport'))
);
const VendorCashFlowReports = Loadable(
  lazy(() => import('../pages/dashboard/VendorCashFlowReports'))
);
const Management = Loadable(
  lazy(() => import('../pages/dashboard/Management'))
);
const FinancialSealControl = Loadable(
  lazy(() => import('../pages/dashboard/FinancialSealControl'))
);
const QuotesConversionReports = Loadable(
  lazy(() => import('../pages/dashboard/QuotesConversionReports'))
);
const MarketingChannelQuotesList = Loadable(
  lazy(() => import('../pages/dashboard/MarketingChannelQuotesList'))
);

// COMPANY

const CompanyList = Loadable(
  lazy(() => import('../pages/dashboard/CompanyPage'))
);
const Portfolio = Loadable(
  lazy(() => import('../pages/dashboard/CompanyAMPortfolio'))
);
const Opportunities = Loadable(
  lazy(() => import('../pages/dashboard/CompanyBDPage'))
);
const CompanyCreate = Loadable(
  lazy(() => import('../pages/dashboard/CompanyCreate'))
);
const CompanyEdit = Loadable(
  lazy(() => import('../pages/dashboard/CompanyEdit'))
);
const CompanyDetails = Loadable(
  lazy(() => import('../pages/dashboard/CompanyDetails'))
);

// INTERPRETATION PROJECT

const InterpretationProjectList = Loadable(
  lazy(() => import('../pages/dashboard/InterpretationProjectList'))
);
const InterpretationProjectCreate = Loadable(
  lazy(() => import('../pages/dashboard/InterpretationProjectCreate'))
);
const InterpretationProjectEdit = Loadable(
  lazy(() => import('../pages/dashboard/InterpretationProjectEdit'))
);
const InterpretationProjectDetails = Loadable(
  lazy(() => import('../pages/dashboard/InterpretationProjectDetails'))
);
const InterpretationProjectBooking = Loadable(
  lazy(() => import('../pages/dashboard/InterpretationProjectBooking'))
);

// MEDIATION PROJECT (CI-MC) — 1:1 clone of the interpretation project stack

const MediationProjectList = Loadable(
  lazy(() => import('../pages/dashboard/MediationProjectList'))
);
const MediationProjectCreate = Loadable(
  lazy(() => import('../pages/dashboard/MediationProjectCreate'))
);
const MediationProjectEdit = Loadable(
  lazy(() => import('../pages/dashboard/MediationProjectEdit'))
);
const MediationProjectDetails = Loadable(
  lazy(() => import('../pages/dashboard/MediationProjectDetails'))
);
const MediationProjectBooking = Loadable(
  lazy(() => import('../pages/dashboard/MediationProjectBooking'))
);

//INTERPRETATION QUOTES

const InterpretationQuoteList = Loadable(
  lazy(() => import('../pages/dashboard/InterpretationQuoteList'))
);
const InterpretationQuoteCreate = Loadable(
  lazy(() => import('../pages/dashboard/InterpretationQuoteCreate'))
);
const InterpretationQuoteEdit = Loadable(
  lazy(() => import('../pages/dashboard/InterpretationQuoteEdit'))
);
const InterpretationQuoteDetails = Loadable(
  lazy(() => import('../pages/dashboard/InterpretationQuoteDetails'))
);

// ACADEMY
const AcademyProjectList = Loadable(
  lazy(() => import('../pages/dashboard/AcademyProjectList'))
);
const AcademyProjectCreate = Loadable(
  lazy(() => import('../pages/dashboard/AcademyProjectCreate'))
);
const AcademyProjectEdit = Loadable(
  lazy(() => import('../pages/dashboard/AcademyProjectEdit'))
);
const AcademyProjectDetails = Loadable(
  lazy(() => import('../pages/dashboard/AcademyProjectDetails'))
);
const AcademyQuoteList = Loadable(
  lazy(() => import('../pages/dashboard/AcademyQuoteList'))
);
const AcademyQuoteCreate = Loadable(
  lazy(() => import('../pages/dashboard/AcademyQuoteCreate'))
);
const AcademyQuoteEdit = Loadable(
  lazy(() => import('../pages/dashboard/AcademyQuoteEdit'))
);
const AcademyQuoteDetails = Loadable(
  lazy(() => import('../pages/dashboard/AcademyQuoteDetails'))
);

// SOFTWARE DEVELOPMENT
const SoftwareDevProjectList = Loadable(
  lazy(() => import('../pages/dashboard/SoftwareDevProjectList'))
);
const SoftwareDevProjectCreate = Loadable(
  lazy(() => import('../pages/dashboard/SoftwareDevProjectCreate'))
);
const SoftwareDevProjectEdit = Loadable(
  lazy(() => import('../pages/dashboard/SoftwareDevProjectEdit'))
);
const SoftwareDevProjectDetails = Loadable(
  lazy(() => import('../pages/dashboard/SoftwareDevProjectDetails'))
);
const SoftwareDevQuoteList = Loadable(
  lazy(() => import('../pages/dashboard/SoftwareDevQuoteList'))
);
const SoftwareDevQuoteCreate = Loadable(
  lazy(() => import('../pages/dashboard/SoftwareDevQuoteCreate'))
);
const SoftwareDevQuoteEdit = Loadable(
  lazy(() => import('../pages/dashboard/SoftwareDevQuoteEdit'))
);
const SoftwareDevQuoteDetails = Loadable(
  lazy(() => import('../pages/dashboard/SoftwareDevQuoteDetails'))
);

//Brochure
const BrochureList = Loadable(
  lazy(() => import('../pages/dashboard/BrochureList'))
);
const BrochureCreate = Loadable(
  lazy(() => import('../pages/dashboard/BrochureCreate'))
);
const BrochureEdit = Loadable(
  lazy(() => import('../pages/dashboard/BrochureEdit'))
);
const BrochureDetails = Loadable(
  lazy(() => import('../pages/dashboard/BrochureDetails'))
);
const BrochureAiList = Loadable(
  lazy(() => import('../pages/dashboard/BrochureAiList'))
);
const BrochureAiDetail = Loadable(
  lazy(() => import('../pages/dashboard/BrochureAiDetail'))
);

//Expense
const ExpenseList = Loadable(
  lazy(() => import('../pages/dashboard/ExpenseList'))
);
const ExpenseCreate = Loadable(
  lazy(() => import('../pages/dashboard/ExpenseCreate'))
);
const ExpenseEdit = Loadable(
  lazy(() => import('../pages/dashboard/ExpenseEdit'))
);
const ExpenseDetails = Loadable(
  lazy(() => import('../pages/dashboard/ExpenseDetails'))
);

//Budget
const BudgetList = Loadable(
  lazy(() => import('../pages/dashboard/BudgetList'))
);
const BudgetCreate = Loadable(
  lazy(() => import('../pages/dashboard/BudgetCreate'))
);
const CreditNoteCreate = Loadable(
  lazy(() => import('../pages/dashboard/CreditNoteCreate'))
);
const CreditNoteEdit = Loadable(
  lazy(() => import('../pages/dashboard/CreditNoteEdit'))
);
const CreditNoteDetails = Loadable(
  lazy(() => import('../pages/dashboard/CreditNoteDetails'))
);

// VIDEO CONFERENCE PROJECT

const VideoConferenceProjectList = Loadable(
  lazy(() => import('../pages/dashboard/VideoConferenceProjectList'))
);
const VideoConferenceProjectCreate = Loadable(
  lazy(() => import('../pages/dashboard/VideoConferenceProjectCreate'))
);
const VideoConferenceProjectEdit = Loadable(
  lazy(() => import('../pages/dashboard/VideoConferenceProjectEdit'))
);
const VideoConferenceProjectDetails = Loadable(
  lazy(() => import('../pages/dashboard/VideoConferenceProjectDetails'))
);

// REPORT
const AMReport = Loadable(lazy(() => import('../pages/dashboard/Reports')));
// LINGUIST ALLOCATION

const BarzanoAllocationResponse = Loadable(
  lazy(() =>
    import('../pages/linguist/linguistAllocation/BarzanoAllocationResponse')
  )
);
const TranslationAllocationResponse = Loadable(
  lazy(() =>
    import('../pages/linguist/linguistAllocation/TranslationAllocationResponse')
  )
);
const InterpretationAllocationResponse = Loadable(
  lazy(() =>
    import(
      '../pages/linguist/linguistAllocation/InterpretationAllocationResponse'
    )
  )
);
// LINGUIST
const TermsAndConditions = Loadable(
  lazy(() => import('../pages/linguist/TermsAndConditions'))
);
const TravelPolicy = Loadable(
  lazy(() => import('../pages/linguist/TravelPolicy'))
);
const TranslationProjects = Loadable(
  lazy(() => import('../pages/linguist/TranslationProjects'))
);
const InterpretationProjects = Loadable(
  lazy(() => import('../pages/linguist/InterpretationProjects'))
);
const MediatorPortalRedirect = Loadable(
  lazy(() => import('../pages/linguist/MediatorPortalRedirect'))
);
const ProjectView = Loadable(
  lazy(() => import('../pages/linguist/ProjectView'))
);
const VerifyEmail = Loadable(
  lazy(() => import('../pages/linguist/VerifyEmail'))
);
const UpdatePassword = Loadable(
  lazy(() => import('../pages/linguist/UpdatePassword'))
);
const LinguistDashboard = Loadable(
  lazy(() => import('../pages/linguist/Dashboard'))
);
const LinguistProfile = Loadable(
  lazy(() => import('../pages/linguist/ViewProfile'))
);
const LinguistProjects = Loadable(
  lazy(() => import('../pages/linguist/Projects'))
);
const LinguistPaymentInfo = Loadable(
  lazy(() => import('../pages/linguist/PaymentInfo'))
);
// const Services = Loadable(lazy(() => import('../pages/linguist/Services')));
const Services = Loadable(lazy(() => import('../pages/linguist/Services')));
const TechnicianSheets = Loadable(
  lazy(() => import('../pages/technician/TechnicianSheetList'))
);
const TechnicianDetails = Loadable(
  lazy(() => import('../pages/technician/TechnicianDetails'))
);
const MarketingChannelList = Loadable(
  lazy(() => import('../pages/marketeer/MarketingChannelList'))
);
const FormattingFilesList = Loadable(
  lazy(() => import('../pages/formatter/FormattingFilesList'))
);
const FormattingFileDetails = Loadable(
  lazy(() => import('../pages/formatter/FormattingFileDetails'))
);
const ProjectsCalendar = Loadable(
  lazy(() => import('../pages/formatter/ProjectsCalendar'))
);
const ExpenseReport = Loadable(
  lazy(() => import('../pages/dashboard/ExpenseReport'))
);
const TreasuryChart = Loadable(
  lazy(() => import('../pages/dashboard/TreasuryChart'))
);
const BarzanoInvoiceCreate = Loadable(
  lazy(() => import('../pages/dashboard/ClientInvoiceCreate'))
);
const LYCIClientInvoiceCreate = Loadable(
  lazy(() => import('../pages/dashboard/LYCIClientInvoiceCreate'))
);
const BarzanoInvoiceEdit = Loadable(
  lazy(() => import('../pages/dashboard/ClientInvoiceEdit'))
);
const InvoiceCreate = Loadable(
  lazy(() => import('../pages/dashboard/InvoiceCreate'))
);
const BarzanoInvoiceView = Loadable(
  lazy(() => import('../pages/dashboard/ClientInvoiceDetails'))
);
const BarzanoInvoiceList = Loadable(
  lazy(() => import('../pages/dashboard/ClientInvoiceList'))
);
const TechnicianInvoice = Loadable(
  lazy(() => import('../pages/technician/InvoiceList'))
);
const TechnicianInvoiceDetail = Loadable(
  lazy(() => import('../pages/technician/InvoiceDetails'))
);
const TechnicianInvoiceCreate = Loadable(
  lazy(() => import('../pages/technician/InvoiceCreate'))
);
const TechnicianInventoryDetails = Loadable(
  lazy(() => import('../pages/technician/InventoryDetails'))
);
const TechnicianInventoryList = Loadable(
  lazy(() => import('../pages/technician/InventoryList'))
);
const TechnicianInventoryCreate = Loadable(
  lazy(() => import('../pages/technician/InventoryCreate'))
);
const TechnicianInventoryEdit = Loadable(
  lazy(() => import('../pages/technician/InventoryEdit'))
);
// USER
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const UserAccount = Loadable(
  lazy(() => import('../pages/dashboard/UserAccount'))
);
const EmailConversations = Loadable(
  lazy(() => import('../pages/dashboard/EmailConversations'))
);
const EmailMailboxAdmin = Loadable(
  lazy(() => import('../pages/dashboard/EmailMailboxAdmin'))
);
const UserCreate = Loadable(
  lazy(() => import('../pages/dashboard/UserCreate'))
);
const UserEdit = Loadable(lazy(() => import('../pages/dashboard/UserEdit')));

// Inventory

const InventoryList = Loadable(
  lazy(() => import('../pages/dashboard/InventoryList'))
);
const InventoryCreate = Loadable(
  lazy(() => import('../pages/dashboard/InventoryCreate'))
);
const InventoryEdit = Loadable(
  lazy(() => import('../pages/dashboard/InventoryEdit'))
);
const InventoryDetails = Loadable(
  lazy(() => import('../pages/dashboard/InventoryDetails'))
);
const InventoryOverview = Loadable(
  lazy(() => import('../pages/dashboard/InventoryOverview'))
);
const RequestList = Loadable(
  lazy(() => import('../pages/dashboard/RequestsList'))
);
const RequestsDetailPage = Loadable(
  lazy(() => import('../pages/dashboard/RequestsDetailPage'))
);

// TEST RENDER PAGE BY Type
const PermissionDenied = Loadable(
  lazy(() => import('../pages/dashboard/PermissionDenied'))
);

const VendorPermissionDenied = Loadable(
  lazy(() => import('../pages/linguist/PermissionDenied'))
);
//OCR

const OcrReport = Loadable(lazy(() => import('../pages/dashboard/Ocr')));

//TextToSpeech

// const TextToSpeech = Loadable(lazy(() => import('../pages/dashboard/TextToSpeech')));

const TwilioCall = Loadable(
  lazy(() => import('../pages/dashboard/TwilioCall'))
);
const PrivateClientsReport = Loadable(
  lazy(() => import('../pages/dashboard/PrivateClientsReport'))
);

const CallRecordsList = Loadable(
  lazy(() => import('../pages/dashboard/CallRecordsList'))
);

// Tickets
const TicketSupport = Loadable(
  lazy(() => import('../pages/dashboard/TicketSupport'))
);
const TicketCreate = Loadable(
  lazy(() => import('../pages/dashboard/TicketCreate'))
);
const TicketEdit = Loadable(
  lazy(() => import('../pages/dashboard/TicketEdit'))
);
const TicketList = Loadable(
  lazy(() => import('../pages/dashboard/TicketList'))
);
const TicketKanban = Loadable(
  lazy(() => import('../pages/dashboard/TicketKanban'))
);
const TicketDetails = Loadable(
  lazy(() => import('../pages/dashboard/TicketDetails'))
);

// MAIN

const Page404 = Loadable(lazy(() => import('../pages/Page404')));
const HomePage = Loadable(lazy(() => import('../pages/HomePage')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));

// Client
const Dashboard = Loadable(lazy(() => import('../pages/client/Dashboard')));
// const BarzanoCalendar = Loadable(lazy(() => import('../pages/client/BarzanoCalendar')));
const EditClient = Loadable(lazy(() => import('../pages/client/EditProfile')));
const InstantQuote = Loadable(
  lazy(() => import('../pages/client/InstantQuote'))
);
const OrderList = Loadable(lazy(() => import('../pages/client/OrderList')));
const OrderDetails = Loadable(
  lazy(() => import('../pages/client/OrderDetails'))
);
const BookInterpreter = Loadable(
  lazy(() => import('../pages/client/book/BookInterpreter'))
);
const InterpreterPayment = Loadable(
  lazy(() => import('../pages/client/bookInterpreter/Payment'))
);
const InterpreterOrderNow = Loadable(
  lazy(() => import('../pages/client/bookInterpreter/OrderNow'))
);
const InterpreterGetQuote = Loadable(
  lazy(() => import('../pages/client/bookInterpreter/GetQuote'))
);
const BookWebConference = Loadable(
  lazy(() => import('../pages/client/book/BookWebConference'))
);
const WebConferencePayment = Loadable(
  lazy(() => import('../pages/client/bookWebConference/Payment'))
);
const WebConferenceOrderNow = Loadable(
  lazy(() => import('../pages/client/bookWebConference/OrderNow'))
);
const WebConferenceGetQuote = Loadable(
  lazy(() => import('../pages/client/bookWebConference/GetQuote'))
);
const BookTranslator = Loadable(
  lazy(() => import('../pages/client/book/BookTranslator'))
);
const TranslatorPayment = Loadable(
  lazy(() => import('../pages/client/bookTranslator/Payment'))
);
const TranslatorOrderNow = Loadable(
  lazy(() => import('../pages/client/bookTranslator/OrderNow'))
);
const TranslatorGetQuote = Loadable(
  lazy(() => import('../pages/client/bookTranslator/GetQuote'))
);

const CostReport = Loadable(
  lazy(() => import('../pages/dashboard/CostReport'))
);
const ROIAnalysis = Loadable(
  lazy(() => import('../pages/dashboard/ROIAnalysis'))
);
const AMReports = Loadable(lazy(() => import('../pages/dashboard/AMReports')));
const PMReports = Loadable(lazy(() => import('../pages/dashboard/PMReports')));

// These twelve were static imports and travelled in the entry bundle with
// everything they pull in (xlsx, antd, the mock city list, the logs model).
// They load on demand like every other route.
const PrivacyPolicy = Loadable(
  lazy(() => import('../pages/linguist/PrivacyPolicy'))
);
const PatentReports = Loadable(
  lazy(() => import('../pages/dashboard/PatentReports'))
);
const PreBookingResponse = Loadable(
  lazy(() => import('../pages/linguist/PreBookingResponse'))
);
const BookingResponse = Loadable(
  lazy(() => import('../pages/linguist/BookingResponse'))
);
const EmailTemplateList = Loadable(
  lazy(() => import('../pages/dashboard/EmailTemplateList'))
);
const EmailTemplateCreate = Loadable(
  lazy(() => import('../pages/dashboard/EmailTemplateCreate'))
);
const EmailTemplateEdit = Loadable(
  lazy(() => import('../pages/dashboard/EmailTemplateEdit'))
);
const EmailTemplateView = Loadable(
  lazy(() => import('../pages/dashboard/EmailTemplateView'))
);
const KPIList = Loadable(lazy(() => import('../pages/dashboard/KPIList')));
const KPICreate = Loadable(lazy(() => import('../pages/dashboard/KPICreate')));
const ServicesMgtList = Loadable(
  lazy(() => import('../pages/dashboard/ServicesMgtList'))
);
const ServiceViewPage = Loadable(
  lazy(() => import('../pages/dashboard/ServiceView'))
);
